/**
 * Client-facing wallet-verification API wrappers.
 *
 * Unlike `clientPositions.ts`, these wrappers do **not** go through the
 * generic `orgApiFetch` because the wallet-verification flow needs the
 * stable `code` field from the API's error envelope (`{ error, code }`)
 * to map to actionable copy in the form. Instead we use `orgAuthFetch`
 * directly and parse both branches ourselves.
 *
 * See `walletVerificationErrors.ts` for the code → copy mapping.
 */

import { orgAuthFetch } from "@solomon-apps/shared";

import { CLIENT_OAUTH_CONFIG } from "@/constants/oauthConfig";

import type { PaginatedResponse } from "./orgApiFetch";
import { mockApiFetch } from "./sim/mockApi";
import { isSimMode } from "./sim/simMode";

export interface WalletView {
    uuid: string;
    address: string;
    label: string | null;
    walletSourceKind: "eoa" | "multisig-squads";
    multisigProgramId: string | null;
    vaultAddress: string | null;
    verifiedSignerAddress: string | null;
    verificationMethod: "signMessage" | "microTransfer" | null;
    verifiedAt: string | null;
    createdAt: string;
}

export const WALLET_VERIFICATION_ERROR_CODES = {
    CHALLENGE_NOT_FOUND: "CHALLENGE_NOT_FOUND",
    CHALLENGE_EXPIRED: "CHALLENGE_EXPIRED",
    CHALLENGE_CONSUMED: "CHALLENGE_CONSUMED",
    SIGNATURE_INVALID: "SIGNATURE_INVALID",
    SIGNER_MUST_MATCH_WALLET: "SIGNER_MUST_MATCH_WALLET",
    NOT_A_SQUADS_VAULT: "NOT_A_SQUADS_VAULT",
    UNSUPPORTED_SQUADS_VERSION: "UNSUPPORTED_SQUADS_VERSION",
    LOOKUP_FAILED: "LOOKUP_FAILED",
    SIGNER_NOT_MULTISIG_MEMBER: "SIGNER_NOT_MULTISIG_MEMBER",
    SIGNER_LACKS_VERIFY_PERMISSION: "SIGNER_LACKS_VERIFY_PERMISSION",
    WALLET_ALREADY_REGISTERED: "WALLET_ALREADY_REGISTERED",
    CLIENT_NOT_FOUND: "CLIENT_NOT_FOUND",
    ISSUER_ORG_NOT_FOUND: "ISSUER_ORG_NOT_FOUND"
} as const;

export type WalletVerificationErrorCode =
    (typeof WALLET_VERIFICATION_ERROR_CODES)[keyof typeof WALLET_VERIFICATION_ERROR_CODES];

/**
 * Thrown by `issueWalletChallenge` / `createClientWallet` /
 * `fetchClientWallets` when the API returns a non-2xx response that
 * carries a known `code`. Callers should `instanceof`-check and map
 * `.code` via `WALLET_VERIFICATION_ERROR_MESSAGES`.
 */
export class WalletServiceError extends Error {
    constructor(
        readonly code: WalletVerificationErrorCode | "UNKNOWN",
        message: string
    ) {
        super(message);
        this.name = "WalletServiceError";
    }
}

interface ApiErrorBody {
    error?: string;
    message?: string;
    code?: string;
}

function isWalletVerificationErrorCode(value: unknown): value is WalletVerificationErrorCode {
    return typeof value === "string" && value in WALLET_VERIFICATION_ERROR_CODES;
}

async function readBodyOrThrow<T>(response: Response): Promise<T> {
    if (response.ok) {
        return response.json() as Promise<T>;
    }
    const body = (await response.json().catch((): null => null)) as ApiErrorBody | null;
    const message = body?.message ?? body?.error ?? `Request failed: ${response.status}`;
    const code = isWalletVerificationErrorCode(body?.code) ? body.code : "UNKNOWN";
    throw new WalletServiceError(code, message);
}

async function walletApiFetch<T>(path: string, init?: RequestInit): Promise<T> {
    if (isSimMode()) {
        const mock = await mockApiFetch(init?.method ?? "GET", path, init);
        if (mock.hit) return mock.value as T;
    }
    const url = `${CLIENT_OAUTH_CONFIG.apiBase}${path}`;
    return orgAuthFetch(url, init, CLIENT_OAUTH_CONFIG).then(readBodyOrThrow<T>);
}

// ─── Endpoints ───────────────────────────────────────────────────────────

export interface IssueWalletChallengeResponse {
    nonce: string;
    expiresAt: string;
    /**
     * Canonical message the user signs verbatim. Server templates this
     * with the resolved issuerOrgSlug; the frontend MUST NOT modify it
     * — any drift will fail signature verification.
     */
    message: string;
}

/**
 * Mint a single-use challenge for the (client, wallet, signer) tuple and
 * receive the canonical message to sign. The challenge has a 10-minute
 * TTL and can be redeemed at most once via `createClientWallet`.
 */
export function issueWalletChallenge(args: {
    walletAddress: string;
    signerAddress: string;
}): Promise<IssueWalletChallengeResponse> {
    return walletApiFetch<IssueWalletChallengeResponse>("/api/org/client/wallets/challenge", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(args)
    });
}

/**
 * Verify ownership of a wallet via a previously-issued challenge and
 * persist the verified wallet on the calling client's catalog.
 *
 * - `walletAddress` is the **pasted** address (vault PDA for multisig,
 *   EOA pubkey otherwise) — the same value used when minting the
 *   challenge. Server canonicalizes to the multisig PDA after signature
 *   verification.
 * - `sourceKind` is NOT a request field. The backend derives it via an
 *   on-curve check; trusting a frontend hint would let callers skip the
 *   multisig membership check.
 */
export function createClientWallet(args: {
    walletAddress: string;
    signerAddress: string;
    nonce: string;
    signature: string;
    signedMessage?: string;
    label?: string | null;
}): Promise<WalletView> {
    return walletApiFetch<WalletView>("/api/org/client/wallets", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(args)
    });
}

/**
 * List the calling client's verified wallets (`verifiedAt desc`). No
 * `signerAddress` filter — every signer of the client sees every client
 * wallet. Submit-time `assertMember` remains the security boundary.
 */
export function fetchClientWallets(args?: {
    limit?: number;
    offset?: number;
    page?: number;
}): Promise<PaginatedResponse<WalletView>> {
    const params = new URLSearchParams();
    if (args?.limit !== undefined) params.set("limit", String(args.limit));
    if (args?.offset !== undefined) params.set("offset", String(args.offset));
    if (args?.page !== undefined) params.set("page", String(args.page));
    const qs = params.toString();
    return walletApiFetch<PaginatedResponse<WalletView>>(`/api/org/client/wallets${qs ? `?${qs}` : ""}`);
}
